import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, type ArticleType, type CardView, type Evidence, type SourceInfo, type SourceRole } from '../api';
import { formatDateTime } from '../format';

/**
 * The source catalogue, loaded once per session. Cards use it to mark official
 * and parliamentary sources; without it (still loading, or failed) cards simply
 * show no role label — the source names themselves come with the card.
 */
const Ctx = createContext<Map<string, SourceInfo>>(new Map());

export function SourceCatalogProvider({ children }: { children: ReactNode }) {
  const [map, setMap] = useState<Map<string, SourceInfo>>(new Map());
  useEffect(() => {
    let live = true;
    api
      .sources()
      .then((r) => live && setMap(new Map(r.sources.map((s) => [s.id, s]))))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return <Ctx.Provider value={map}>{children}</Ctx.Provider>;
}

export function useSourceCatalog() {
  return useContext(Ctx);
}

export const ROLE_LABELS: Record<SourceRole, string> = {
  journalism: 'Medien',
  official: 'Bund',
  parliament: 'Parlamentsdaten',
  index: 'Verzeichnisse',
  events: 'Veranstaltungen',
};

/** Labels shown only when the type is stated; a report needs no label. */
export const ARTICLE_TYPE_LABELS: Partial<Record<ArticleType, string>> = {
  opinion: 'Meinung',
  commentary: 'Kommentar',
  interview: 'Interview',
  debate: 'Debatte',
  official_communication: 'Mitteilung des Bundes',
  // Parliament records are data from the Parliamentary Services, not an
  // official publication; the label says exactly that.
  parliamentary_record: 'Parlamentsdaten',
};

export type RecordKind = 'parliament' | 'federal' | null;

/** Whether an evidence item is a parliament record or a federal communication. */
export function recordKind(e: Evidence, catalog: Map<string, SourceInfo>): RecordKind {
  const role = catalog.get(e.source_id)?.role;
  const t = e.attribution?.article_type;
  if (role === 'parliament' || t === 'parliamentary_record') return 'parliament';
  if (role === 'official' || t === 'official_communication') return 'federal';
  return null;
}

/** Procedure facts; event_date has day precision and is shown as a date only. */
export function parliamentFacts(e: Evidence): string[] {
  const a = e.attribution ?? {};
  const out: string[] = [];
  if (a.procedural_stage) out.push(a.procedural_stage);
  if (a.council) out.push(a.council);
  if (a.event_date) out.push(dayOnly(a.event_date));
  return out;
}

export function dayOnly(v: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
}

/** "Datenbezug: 23. Sept. 2026, 05:10" from the revision's retrieval time. */
export function dataRetrieval(e: Evidence): string | null {
  return e.fetched_at ? `Datenbezug: ${formatDateTime(e.fetched_at)}` : null;
}

/** Role and article-type labels for a card's source line, deduplicated. */
export function useCardLabels(view: CardView): string[] {
  const catalog = useSourceCatalog();
  const out: string[] = [];
  const add = (l: string | undefined) => {
    if (l && !out.includes(l)) out.push(l);
  };
  for (const e of view.version.evidence ?? []) {
    const kind = recordKind(e, catalog);
    if (kind === 'federal') add('Mitteilung des Bundes');
    if (kind === 'parliament') add('Parlamentsdaten');
    const t = e.attribution?.article_type;
    if (t && t !== 'official_communication' && t !== 'parliamentary_record') add(ARTICLE_TYPE_LABELS[t]);
  }
  return out;
}

/**
 * Notes a parliament record or federal communication carries wherever the card
 * appears: procedure facts, issuer, retrieval time and the source's attribution
 * text verbatim. The Parliament webservice terms require the attribution and the
 * "Datenbezug" with every record shown.
 */
export function RecordNotes({ view }: { view: CardView }) {
  const catalog = useSourceCatalog();
  const lines: Array<{ key: string; facts?: string; issuer?: string; fetched?: string | null }> = [];
  const terms: string[] = [];
  const seen = new Set<string>();
  for (const e of view.version.evidence ?? []) {
    const kind = recordKind(e, catalog);
    if (!kind) continue;
    const key = e.item_id || e.revision_id;
    if (seen.has(key)) continue;
    seen.add(key);
    if (kind === 'parliament') {
      const f = parliamentFacts(e);
      lines.push({ key, facts: f.length ? f.join(' · ') : undefined, fetched: dataRetrieval(e) ?? 'Datenbezug: Zeitpunkt nicht erfasst' });
    } else if (e.attribution?.issuer) {
      lines.push({ key, issuer: e.attribution.issuer });
    }
    const t = e.attribution?.terms_attribution;
    if (t && !terms.includes(t)) terms.push(t);
  }
  if (lines.length === 0 && terms.length === 0) return null;
  // Several items of one affair can carry identical facts; show each line once.
  const shown = lines.filter((l, i) => lines.findIndex((o) => o.facts === l.facts && o.fetched === l.fetched && o.issuer === l.issuer) === i);
  return (
    <div className="record-notes">
      {shown.map((l) => (
        <p key={l.key}>
          {l.facts && <strong>{l.facts}</strong>}
          {l.facts && l.fetched ? ' · ' : ''}
          {l.fetched}
          {l.issuer && <>Herausgegeben von {l.issuer}</>}
        </p>
      ))}
      {terms.map((t) => (
        <p key={t} className="terms">
          {t}
        </p>
      ))}
    </div>
  );
}
